// src/pages/admin/emails/AdminEmailSends.tsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabaseClient";
import SendDetailDialog from "./components/SendDetailDialog";

type SendRow = {
  id: string;
  queue_id: string | null;
  user_id: string;
  template_id: string;
  provider: string;
  provider_message_id: string | null;
  sent_at: string | null;
  failed_at: string | null;
  error: string | null;
  created_at: string;
  email_templates: { id: string; name: string } | null;
  profiles: { id: string; email: string | null; first_name: string | null } | null;
};

type MetricCounts = Record<
  string,
  { opened: number; clicked: number; delivered: number; bounced: number; complained: number }
>;

type TemplateOpt = { id: string; name: string };

export default function AdminEmailSends() {
  // Filterzustand
  const [q, setQ] = useState("");
  const [templateId, setTemplateId] = useState<string>("all");
  const [result, setResult] = useState<"all" | "sent" | "failed">("all");
  const [dateFrom, setDateFrom] = useState<string | "">(defaultFrom());
  const [dateTo, setDateTo] = useState<string | "">(defaultTo());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Daten
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<SendRow[]>([]);
  const [templates, setTemplates] = useState<TemplateOpt[]>([]);
  const [metrics, setMetrics] = useState<MetricCounts>({});
  const [detail, setDetail] = useState<SendRow | null>(null);

  // Templates laden (für Filter)
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from("email_templates").select("id,name").order("name");
      if (!error) setTemplates((data ?? []) as TemplateOpt[]);
    })();
  }, []);

  // Sends laden (Seite)
  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, result, dateFrom, dateTo, page, pageSize]);

  async function fetchPage() {
    setLoading(true);
    const from = page * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from("email_sends")
      .select(
        `
        id, queue_id, user_id, template_id, provider, provider_message_id, sent_at, failed_at, error, created_at,
        email_templates ( id, name ),
        profiles ( id, email, first_name )
        `,
        { count: "exact" }
      )
      .order("created_at", { ascending: false })
      .range(from, to);

    // Zeitraum (filtert auf sent_at, sonst auf created_at fallback)
    if (dateFrom) {
      query = query.or(`sent_at.gte.${iso(dateFrom, "start")},and(failed_at.is.null,created_at.gte.${iso(dateFrom, "start")})`);
    }
    if (dateTo) {
      query = query.or(`sent_at.lte.${iso(dateTo, "end")},and(failed_at.is.null,created_at.lte.${iso(dateTo, "end")})`);
    }

    if (templateId !== "all") query = query.eq("template_id", templateId);
    if (result === "sent") query = query.not("sent_at", "is", null);
    if (result === "failed") query = query.not("failed_at", "is", null);

    const { data, error } = await query;
    if (error) {
      console.error(error);
      setRows([]);
      setMetrics({});
      setLoading(false);
      return;
    }

    let list = (data ?? []) as SendRow[];

    // Clientseitige Volltextsuche (einfach): E-Mail / Template-Name / Provider-ID
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((r) => {
        // KORREKTUR: Zugriff auf das erste Element des Arrays
        const email = r.profiles?.email?.toLowerCase() ?? "";
        const tname = r.email_templates?.name?.toLowerCase() ?? "";
        const pmid = r.provider_message_id?.toLowerCase() ?? "";
        const fn = r.profiles?.first_name?.toLowerCase() ?? "";
        return email.includes(needle) || tname.includes(needle) || pmid.includes(needle) || fn.includes(needle);
      });
    }

    setRows(list);

    // Metriken für sichtbare IDs laden (eine Abfrage, dann clientseitig gruppieren)
    const ids = list.map((r) => r.id);
    if (ids.length > 0) {
      const { data: mrows, error: merr } = await supabase
        .from("email_metrics")
        .select("send_id, event")
        .in("send_id", ids);
      if (!merr) {
        const agg: MetricCounts = {};
        for (const id of ids) {
          agg[id] = { opened: 0, clicked: 0, delivered: 0, bounced: 0, complained: 0 };
        }
        (mrows ?? []).forEach((m: any) => {
          if (!agg[m.send_id]) {
            agg[m.send_id] = { opened: 0, clicked: 0, delivered: 0, bounced: 0, complained: 0 };
          }
          const ev = String(m.event);
          if (ev === "opened") agg[m.send_id].opened++;
          if (ev === "clicked") agg[m.send_id].clicked++;
          if (ev === "delivered") agg[m.send_id].delivered++;
          if (ev === "bounced") agg[m.send_id].bounced++;
          if (ev === "complained") agg[m.send_id].complained++;
        });
        setMetrics(agg);
      } else {
        setMetrics({});
      }
    } else {
      setMetrics({});
    }

    setLoading(false);
  }

  const hasAny = rows.length > 0;

  return (
    <div className="max-w-7xl mx-auto mt-10 p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-sky-400">📊 Versandhistorie</h1>
        <div className="flex items-center gap-3">
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              // keine weitere serverseitige Abfrage nötig; clientseitig filtern
            }}
            placeholder="Suche (E-Mail, Template, Provider-ID)…"
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded-lg w-80 outline-none focus:border-sky-400 placeholder:text-slate-600"
          />
          <select
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded-lg outline-none focus:border-sky-400"
            value={templateId}
            onChange={(e) => {
              setTemplateId(e.target.value);
              setPage(0);
            }}
          >
            <option value="all">Alle Vorlagen</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>

          <select
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded-lg outline-none focus:border-sky-400"
            value={result}
            onChange={(e) => {
              setResult(e.target.value as any);
              setPage(0);
            }}
          >
            <option value="all">Alle Ergebnisse</option>
            <option value="sent">Erfolg (sent)</option>
            <option value="failed">Fehler (failed)</option>
          </select>

          <input
            type="date"
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded-lg outline-none focus:border-sky-400"
            value={String(dateFrom)}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(0);
            }}
          />
          <input
            type="date"
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded-lg outline-none focus:border-sky-400"
            value={String(dateTo)}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(0);
            }}
          />

          <select
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded-lg outline-none focus:border-sky-400"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <button onClick={fetchPage} className="px-3 py-2 rounded-lg border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors" title="Neu laden">
            🔄
          </button>

          <Link to="/admin/emails" className="text-sky-400 hover:underline">
            Zurück
          </Link>
        </div>
      </div>

      <p className="text-slate-400 mb-4">
        Hier siehst du gesendete E-Mails (und Fehlversuche). Klicke auf „Details“, um Ereignisse wie
        <em> delivered / opened / clicked / bounced / complained</em> zu sehen.
      </p>

      {loading ? (
        <div className="p-6 border border-[#2a3347] rounded-xl bg-[#161b27] text-slate-400">Lade…</div>
      ) : !hasAny ? (
        <div className="p-6 border border-[#2a3347] rounded-xl bg-[#161b27] text-slate-500">Keine Einträge im gewählten Zeitraum.</div>
      ) : (
        <div className="overflow-x-auto border border-[#2a3347] rounded-xl bg-[#161b27]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#1e2639] text-left">
              <tr>
                <th className="px-3 py-3 font-semibold text-slate-300">Zeit</th>
                <th className="px-3 py-3 font-semibold text-slate-300">Empfänger</th>
                <th className="px-3 py-3 font-semibold text-slate-300">Vorlage</th>
                <th className="px-3 py-3 font-semibold text-slate-300">Provider</th>
                <th className="px-3 py-3 font-semibold text-slate-300">Ergebnis</th>
                <th className="px-3 py-3 font-semibold text-slate-300">Metriken</th>
                <th className="px-3 py-3 font-semibold text-slate-300 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const m = metrics[r.id] ?? { opened: 0, clicked: 0, delivered: 0, bounced: 0, complained: 0 };
                const outcome = r.sent_at
                  ? <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-900/30 text-emerald-400">sent</span>
                  : r.failed_at
                  ? <span className="px-2 py-0.5 rounded-full text-xs bg-red-900/30 text-red-400">failed</span>
                  : <span className="px-2 py-0.5 rounded-full text-xs bg-[#2a3347] text-slate-400">—</span>;

                const template = r.email_templates;
                const profile = r.profiles;

                return (
                  <tr key={r.id} className="border-t border-[#2a3347]">
                    <td className="px-3 py-2 text-slate-300">
                      <div>{r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}</div>
                      <div className="text-xs text-slate-500">
                        erstellt: {new Date(r.created_at).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-100">{profile?.email ?? "—"}</div>
                      <div className="text-xs text-slate-500">{profile?.first_name ?? "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-300">{template?.name ?? r.template_id.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-slate-300">
                      <div>{r.provider}</div>
                      {r.provider_message_id && (
                        <div className="text-xs text-slate-500 break-all max-w-[260px]">
                          {r.provider_message_id}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {outcome}
                      {r.error && (
                        <div className="text-xs text-red-400 mt-1 line-clamp-2 max-w-[280px]">
                          {r.error}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <MetricBadges m={m} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => setDetail(r)}
                          className="px-2 py-1 text-sky-400 hover:underline"
                        >
                          Details
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-slate-500">Seite {page + 1}</div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-2 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] disabled:opacity-50 transition-colors"
            disabled={page === 0}
          >
            ← Zurück
          </button>
          <button
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-2 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors"
          >
            Weiter →
          </button>
        </div>
      </div>

      {detail && (
        <SendDetailDialog
          row={detail}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function defaultFrom() {
  // Standard: letzte 7 Tage
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}
function defaultTo() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}
function iso(dateLike: string, edge: "start" | "end") {
  if (!dateLike) return undefined;
  const d = new Date(dateLike + "T00:00:00Z");
  if (edge === "end") d.setUTCHours(23, 59, 59, 999);
  return d.toISOString();
}

function MetricBadges({ m }: { m: MetricCounts[string] }) {
  const chip = (label: string, val: number, classes: string) => (
    <span key={label} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${classes}`}>
      {label} <span className="font-medium">{val}</span>
    </span>
  );
  return (
    <div className="flex flex-wrap gap-1">
      {chip("deliv", m.delivered, "bg-emerald-900/30 text-emerald-400")}
      {chip("open", m.opened, "bg-sky-900/30 text-sky-400")}
      {chip("click", m.clicked, "bg-sky-900/30 text-sky-400")}
      {chip("bounce", m.bounced, "bg-red-900/30 text-red-400")}
      {chip("spam", m.complained, "bg-amber-900/30 text-amber-400")}
    </div>
  );
}